import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { MonitorUp, Upload, Camera, Loader2, TrendingUp, TrendingDown, Minus, ScanLine, Newspaper, Radar, Bell, Zap, Volume2, VolumeX, Bot } from "lucide-react";
import Layout from "@/components/Layout";
import Disclaimer from "@/components/Disclaimer";
import ProbabilityGauge from "@/components/ProbabilityGauge";
import { SignalReasoning } from "@/components/SignalReasoning";
import { SignalEvidence } from "@/components/SignalEvidence";
import { ForecastReport } from "@/components/ForecastReport";
import { TradePlan } from "@/components/TradePlan";
import { BrokerChat } from "@/components/BrokerChat";
import { useVoice } from "@/hooks/useVoice";
import { CreditMeter } from "@/components/CreditMeter";
import { liveSession } from "@/lib/liveSession";
import { useAuth } from "@/context/AuthContext";
import { Mic, MicOff, Volume1, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Copilot() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const canShareScreen = !!navigator.mediaDevices?.getDisplayMedia && !isIOS;
  const LS = liveSession.get();
  const resumed = liveSession.isActive();
  const [sharing, setSharing] = useState(resumed);
  const [preview, setPreview] = useState(null); // base64 image
  const [symbol, setSymbol] = useState(LS.symbol || "");
  const [timeframe, setTimeframe] = useState(LS.timeframe || localStorage.getItem("copilot_timeframe") || "");
  const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w"];
  const [note, setNote] = useState(LS.note || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(resumed ? LS.result : null);
  const [lost, setLost] = useState(LS.lost);
  const [source, setSource] = useState("upload");
  const [autoScan, setAutoScan] = useState(resumed ? LS.autoScan : false);
  const [alerts, setAlerts] = useState(resumed ? LS.alerts : []);
  const scanBusy = useRef(false);
  const ALERT_THRESHOLD = 70;
  const SCAN_OPTIONS = [15, 30, 60];
  const [scanSecs, setScanSecs] = useState(() => { const v = Number(localStorage.getItem("copilot_scan_secs")); return SCAN_OPTIONS.includes(v) ? v : 15; });
  const SCAN_INTERVAL_MS = scanSecs * 1000;
  const setInterval_ = (secs) => { setScanSecs(secs); localStorage.setItem("copilot_scan_secs", String(secs)); toast.success(`Live scan every ${secs}s`); };
  const { user } = useAuth();
  const voice = useVoice(user?.language || "en");
  const lastRef = useRef(resumed ? LS.lastRef : null); // last auto result for change detection
  const brokerRef = useRef(resumed ? LS.brokerSessionId : null);
  const [liveAlert, setLiveAlert] = useState(resumed ? LS.liveAlert : null);
  const [liveLog, setLiveLog] = useState(resumed ? LS.liveLog : []);
  const [scanCount, setScanCount] = useState(resumed ? LS.scanCount : 0);
  const [lastScanAt, setLastScanAt] = useState(resumed ? LS.lastScanAt : null);
  const [voiceQ, setVoiceQ] = useState(null); // {q, a, loading}
  const [brokerRefresh, setBrokerRefresh] = useState(0);
  const [usageKey, setUsageKey] = useState(0);
  const limitHit = useRef(false);

  const L = (user?.language || "en");
  const T = {
    received: { en: "Screen share received. Analyzing your chart now. Please wait a moment.", ta: "உங்கள் screen share கிடைத்தது. உங்கள் chart-ஐ இப்போ analyze பண்ணுறேன். ஒரு நிமிடம் காத்திருங்க.", hi: "स्क्रीन शेयर मिल गया। मैं अभी आपका चार्ट analyze कर रहा हूँ। एक पल रुकिए।", te: "మీ స్క్రీన్ షేర్ అందింది. ఇప్పుడు మీ చార్ట్‌ను విశ్లేషిస్తున్నాను. ఒక్క క్షణం ఆగండి.", ml: "നിങ്ങളുടെ സ്ക്രീൻ ഷെയർ ലഭിച്ചു. ചാർട്ട് ഇപ്പോൾ വിശകലനം ചെയ്യുന്നു. ഒരു നിമിഷം കാത്തിരിക്കൂ." },
    approaching: { en: "Price is now approaching your entry zone. Get ready.", ta: "விலை உங்க entry zone-க்கு நெருங்குது. ரெடியா இருங்க.", hi: "कीमत आपके entry zone के पास आ रही है। तैयार रहिए।", te: "ధర మీ ఎంట్రీ జోన్‌కు చేరుకుంటోంది. సిద్ధంగా ఉండండి.", ml: "വില നിങ്ങളുടെ എൻട്രി സോണിലേക്ക് അടുക്കുന്നു. തയ്യാറായിരിക്കൂ." },
    entry: { en: "Entry point reached at {p}. This is your signal.", ta: "Entry point {p} வந்தாச்சு. இதுதான் உங்க signal.", hi: "Entry point {p} पर पहुँच गया। यही आपका signal है।", te: "ఎంట్రీ పాయింట్ {p} చేరుకుంది. ఇదే మీ సిగ్నల్.", ml: "എൻട്രി പോയിന്റ് {p} എത്തി. ഇതാണ് നിങ്ങളുടെ സിഗ്നൽ." },
    target: { en: "Target hit. {p} reached. Consider exiting your position now.", ta: "Target {p} reach ஆயிடுச்சு. இப்போ exit பண்ணுங்க.", hi: "Target {p} पहुँच गया। अभी exit करने पर विचार करें।", te: "టార్గెట్ {p} చేరుకుంది. ఇప్పుడు ఎగ్జిట్ చేయండి.", ml: "ടാർഗറ്റ് {p} എത്തി. ഇപ്പോൾ എക്സിറ്റ് ചെയ്യുന്നത് പരിഗണിക്കൂ." },
    stop: { en: "Warning. Price is breaking below your stop at {p}. Exit immediately to protect capital.", ta: "எச்சரிக்கை. விலை stop {p}-க்கு கீழே போகுது. உடனே exit பண்ணுங்க.", hi: "चेतावनी। कीमत आपके stop {p} से नीचे जा रही है। पूँजी बचाने के लिए तुरंत exit करें।", te: "హెచ్చరిక. ధర మీ స్టాప్ {p} కింద పడుతోంది. వెంటనే ఎగ్జిట్ చేయండి.", ml: "ജാഗ്രത. വില സ്റ്റോപ്പ് {p} താഴേക്ക് പോകുന്നു. ഉടൻ എക്സിറ്റ് ചെയ്യൂ." },
    urgent: { en: "URGENT. Strong {rec} signal detected. {p} percent win probability. Act within the next 5 minutes.", ta: "அவசரம். வலுவான {rec} signal. {p} சதவீதம் profit chance. அடுத்த 5 நிமிஷத்துல act பண்ணுங்க.", hi: "URGENT. मज़बूत {rec} signal मिला। {p} प्रतिशत win probability। अगले 5 मिनट में action लें।", te: "అత్యవసరం. బలమైన {rec} సిగ్నల్. {p} శాతం విజయావకాశం. 5 నిమిషాల్లో చర్య తీసుకోండి.", ml: "അടിയന്തരം. ശക്തമായ {rec} സിഗ്നൽ. {p} ശതമാനം വിജയസാധ്യത. 5 മിനിറ്റിനുള്ളിൽ പ്രവർത്തിക്കൂ." },
    changed: { en: "Update. The signal changed to {rec} with {p} percent probability.", ta: "Update. Signal இப்போ {rec}-ஆ மாறிடுச்சு, {p} சதவீதம் probability.", hi: "Update। Signal अब {rec} हो गया है, {p} प्रतिशत probability।", te: "అప్‌డేట్. సిగ్నల్ ఇప్పుడు {rec}గా మారింది, {p} శాతం అవకాశం.", ml: "അപ്‌ഡേറ്റ്. സിഗ്നൽ {rec} ആയി മാറി, {p} ശതമാനം സാധ്യത." },
  };
  const say = (key, vars = {}, opts) => {
    let t = (T[key][L] || T[key].en);
    Object.entries(vars).forEach(([k, v]) => { t = t.replace(`{${k}}`, v); });
    voice.speak(t, opts);
    setLiveLog((l) => [{ id: Date.now() + Math.random(), time: new Date().toLocaleTimeString(), text: t, key }, ...l].slice(0, 12));
  };
  const [muted, setMuted] = useState(() => localStorage.getItem("copilot_muted") === "1");
  const audioCtx = useRef(null);
  const [broker, setBroker] = useState(resumed && LS.brokerSessionId ? { sessionId: LS.brokerSessionId, messages: null } : null); // {sessionId, messages, loading}

  const openBroker = async (analysisId) => {
    setBroker({ loading: true });
    try {
      const { data } = await api.post("/broker/sessions", { analysis_id: analysisId, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      brokerRef.current = data.session.session_id;
      if (data.first_message) voice.markSpoken(data.first_message.message_id); // Copilot already voiced this analysis
      setBroker({ sessionId: data.session.session_id, messages: data.first_message ? [data.first_message] : [] });
    } catch (err) {
      brokerRef.current = null;
      setBroker(null);
      toast.error(apiError(err.response?.data?.detail) || "Broker unavailable");
    }
  };

  const toggleMute = () => setMuted((m) => { localStorage.setItem("copilot_muted", m ? "0" : "1"); return !m; });

  const playChime = () => {
    if (muted) return;
    try {
      const ctx = audioCtx.current || (audioCtx.current = new (window.AudioContext || window.webkitAudioContext)());
      if (ctx.state === "suspended") ctx.resume();
      [[880, 0], [1174.66, 0.16], [1567.98, 0.32]].forEach(([freq, at]) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.35);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + at); osc.stop(ctx.currentTime + at + 0.4);
      });
    } catch { /* audio unavailable */ }
  };

  const notifyDesktop = (alert) => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    try {
      new Notification(`${alert.rec} setup · ${alert.prob}% on ${alert.symbol}`, {
        body: "AI Trading Copilot found a high-probability setup. Review before acting.", tag: "copilot-alert", silent: true,
      });
    } catch { /* ignore */ }
  };

  const toggleAutoScan = () => {
    const next = !autoScan;
    setAutoScan(next);
    if (next) {
      if (audioCtx.current?.state === "suspended") audioCtx.current.resume();
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().then((p) => { if (p === "granted") toast.success("Desktop alerts enabled"); });
      }
    }
  };

  const [liveMode, setLiveMode] = useState(resumed ? (LS.mode || "screen") : "screen"); // screen | camera
  const startCamera = () => startShare(false, "camera");

  const startShare = async (reconnect = false, mode = null) => {
    const useCamera = mode === "camera" || (mode === null && reconnect === true && liveSession.get().mode === "camera");
    try {
      const stream = useCamera
        ? await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
        : await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 5 }, audio: false });
      streamRef.current = stream;
      setLiveMode(useCamera ? "camera" : "screen");
      liveSession.setStream(stream);
      liveSession.set({ mode: useCamera ? "camera" : "screen" });
      if (reconnect !== true) liveSession.set({ result: null, alerts: [], liveLog: [], liveAlert: null, brokerSessionId: null, lastRef: null, scanCount: 0, lastScanAt: null });
      setLost(false);
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setSharing(true);
      setSource("screen");
      stream.getVideoTracks()[0].onended = () => { setSharing(false); setAutoScan(false); setLost(true); voice.stopLoop(); voice.stop(); toast.error("Screen share lost — click Reconnect to resume"); };
      if (reconnect !== true) { lastRef.current = null; setScanCount(0); }
      limitHit.current = false;
      setAutoScan(true);
      if (audioCtx.current?.state === "suspended") audioCtx.current.resume();
      if ("Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {});
      if (useCamera) { toast.success("Camera live — hold the phone steady on your chart"); } say("received");
      if (voice.sttSupported) voice.startLoop(handleVoiceQuestion);
      toast.success("Screen share received — AI is analyzing your chart now");
      setTimeout(() => { const b64 = captureFrame(true); if (b64) runAnalyze(b64, true); }, 1500);
    } catch {
      toast.error(useCamera ? "Camera access was denied — allow camera permission in Safari settings" : "Screen share was cancelled or blocked");
    }
  };

  const stopShare = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    liveSession.stop();
    setLost(false);
    setSharing(false);
    setAutoScan(false);
    voice.stopLoop();
    voice.stop();
  };

  const captureFrame = (silent = false) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { if (!silent) toast.error("No video frame yet"); return null; }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const b64 = canvas.toDataURL("image/jpeg", 0.85);
    setPreview(b64);
    setSource("screen");
    if (!silent) toast.success("Frame captured");
    return b64;
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result); setSource("upload");
      if (!canShareScreen) { toast.success("Chart received — analyzing now"); setTimeout(() => runAnalyze(reader.result, false), 50); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const analyze = () => runAnalyze(preview, false);
  const confirmDetection = (sym, tf) => {
    setSymbol(sym); setTimeframe(tf); localStorage.setItem("copilot_timeframe", tf);
    runAnalyze(preview || captureFrame(true), false, { symbol: sym, timeframe: tf });
  };

  const runAnalyze = async (img, isAuto = false, overrides = {}) => {
    if (!img) { if (!isAuto) toast.error("Capture or upload a chart first"); return; }
    if (scanBusy.current) return;
    scanBusy.current = true;
    if (!isAuto) { setLoading(true); setResult(null); setBroker(null); }
    try {
      const { data } = await api.post("/copilot/analyze", { image_base64: img, symbol: overrides.symbol ?? (symbol || null), timeframe: overrides.timeframe ?? (timeframe || null), note: note || null, source: isAuto ? "screen" : source, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      setResult(data);
      setUsageKey((k) => k + 1);
      if (data.usage?.limit_reached && isAuto && !limitHit.current) {
        limitHit.current = true;
        setAutoScan(false);
        toast.warning("Daily AI scan limit reached — live scanning paused", { duration: 8000 });
      }
      const res = data.result;
      const maxp = Math.max(res.buy_probability || 0, res.sell_probability || 0);
      if (isAuto) {
        setScanCount((c) => c + 1);
        setLastScanAt(new Date());
        const prev = lastRef.current;
        const price = data.market?.price ?? res.entry;
        const entry = res.entry, target = res.take_profit, stop = res.stop_loss;
        const isBuy = res.recommendation === "BUY";
        const hot = maxp >= ALERT_THRESHOLD && res.recommendation !== "HOLD";
        if (!prev) {
          voice.speak(res.voice_summary || `${res.recommendation} ${data.symbol || res.detected_symbol || ""}. ${maxp} percent win probability. Entry ${entry}, target ${target}, stop ${stop}.`, { key: `analysis_${data.analysis_id}` });
          setLiveLog((l) => [{ id: Date.now(), time: new Date().toLocaleTimeString(), text: res.voice_summary || res.rationale, key: "analysis" }, ...l].slice(0, 12));
          if (!brokerRef.current) { brokerRef.current = "pending"; openBroker(data.analysis_id); }
        } else {
          if (prev.recommendation !== res.recommendation && res.recommendation !== "HOLD") say("changed", { rec: res.recommendation, p: maxp });
          if (price != null && entry != null && prev.price != null) {
            const near = Math.abs(price - entry) / entry < 0.003;
            const crossed = isBuy ? (prev.price > entry && price <= entry) : (prev.price < entry && price >= entry);
            if (crossed || (near && !prev.nearEntry)) say(crossed ? "entry" : "approaching", { p: entry });
            if (target != null && (isBuy ? price >= target : price <= target) && !(isBuy ? prev.price >= target : prev.price <= target)) say("target", { p: target }, { urgent: true });
            if (stop != null && (isBuy ? price <= stop : price >= stop) && !(isBuy ? prev.price <= stop : prev.price >= stop)) say("stop", { p: stop }, { urgent: true });
          }
        }
        if (hot && (!prev || !prev.hot || prev.recommendation !== res.recommendation)) {
          const alert = { id: Date.now(), time: new Date().toLocaleTimeString(), symbol: data.symbol || res.detected_symbol || "chart", rec: res.recommendation, prob: maxp, reasoning: res.reasoning, entry, target, stop };
          setAlerts((a) => [alert, ...a].slice(0, 8));
          setLiveAlert(alert);
          playChime();
          notifyDesktop(alert);
          say("urgent", { rec: res.recommendation, p: maxp }, { urgent: true });
          toast.success(`${alert.rec} setup · ${maxp}% on ${alert.symbol}`, { duration: 6000 });
        }
        lastRef.current = { recommendation: res.recommendation, price, hot, nearEntry: price != null && entry != null && Math.abs(price - entry) / entry < 0.003 };
      } else {
        toast.success("Analysis complete");
        if (res.voice_summary) voice.speak(res.voice_summary, { key: `analysis_${data.analysis_id}` });
        openBroker(data.analysis_id);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 402) { toast.error("Trial ended — subscribe to continue"); setAutoScan(false); }
      else if (status === 429) { setAutoScan(false); limitHit.current = true; setUsageKey((k) => k + 1); toast.warning(apiError(err.response?.data?.detail), { duration: 8000 }); }
      else if (!isAuto) toast.error(apiError(err.response?.data?.detail) || "Analysis failed");
    } finally {
      scanBusy.current = false;
      if (!isAuto) setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoScan || !sharing) return;
    const id = setInterval(() => {
      if (scanBusy.current) return;
      const b64 = captureFrame(true);
      if (b64) runAnalyze(b64, true);
    }, SCAN_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScan, sharing, symbol, timeframe, note, source, scanSecs]);

  const handleVoiceQuestion = async (q) => {
    setVoiceQ({ q, loading: true });
    try {
      let sid = brokerRef.current && brokerRef.current !== "pending" ? brokerRef.current : null;
      if (!sid) {
        const { data } = await api.post("/broker/sessions", { analysis_id: result?.analysis_id || null, symbol: symbol || null, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
        sid = data.session.session_id; brokerRef.current = sid;
        setBroker({ sessionId: sid, messages: data.first_message ? [data.first_message] : [] });
      }
      const { data } = await api.post(`/broker/sessions/${sid}/messages`, { text: q, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      setVoiceQ({ q, a: data.reply.content });
      voice.speak(data.reply.content, { urgent: true, key: data.reply.message_id });
      setBrokerRefresh((k) => k + 1);
    } catch (err) {
      setVoiceQ({ q, a: null, error: apiError(err.response?.data?.detail) });
      toast.error(apiError(err.response?.data?.detail) || "Broker unavailable");
    }
  };

  const toggleVoiceChat = () => {
    if (voice.loopOn) { voice.stopLoop(); toast("Voice chat stopped"); return; }
    const ok = voice.startLoop(handleVoiceQuestion);
    if (!ok) toast.error("Voice input not supported in this browser — use Chrome");
    else toast.success("Voice chat on — speak after the broker finishes talking");
  };

  // Resume a live session that survived navigation (stream lives in lib/liveSession).
  useEffect(() => {
    if (!resumed) return;
    const stream = LS.stream;
    streamRef.current = stream;
    if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
    setSource("screen");
    stream.getVideoTracks()[0].onended = () => { setSharing(false); setAutoScan(false); setLost(true); voice.stopLoop(); voice.stop(); toast.error("Screen share lost — click Reconnect to resume"); };
    if (LS.voiceLoop && voice.sttSupported) voice.startLoop(handleVoiceQuestion);
    if (LS.autoScan) {
      toast.success("Live session restored — vision analysis resumed");
      setTimeout(() => { if (!scanBusy.current) { const b64 = captureFrame(true); if (b64) runAnalyze(b64, true); } }, 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    liveSession.set({ autoScan, result, alerts, liveLog, liveAlert, scanCount, lastScanAt, symbol, timeframe, note, lastRef: lastRef.current,
      brokerSessionId: brokerRef.current && brokerRef.current !== "pending" ? brokerRef.current : liveSession.get().brokerSessionId, voiceLoop: voice.loopOn });
  }, [autoScan, result, alerts, liveLog, liveAlert, scanCount, lastScanAt, symbol, timeframe, note, voice.loopOn]);

  useEffect(() => () => { liveSession.set({ lastRef: lastRef.current }); }, []);

  const r = result?.result;
  const recColor = r?.recommendation === "BUY" ? "text-emerald-400" : r?.recommendation === "SELL" ? "text-red-400" : "text-amber-400";
  const RecIcon = r?.recommendation === "BUY" ? TrendingUp : r?.recommendation === "SELL" ? TrendingDown : Minus;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <ScanLine className="w-7 h-7 text-emerald-500" />
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">AI Trading Copilot</h1>
            <p className="text-slate-400 text-sm">Share your screen or upload a chart. The AI reads it with computer vision and returns win/loss probabilities.</p>
          </div>
        </div>

        {(sharing || lost) && (
          <div className={`mb-5 rounded-xl border px-4 py-3 flex items-center gap-3 ${lost ? "border-red-500/50 bg-red-500/10" : "border-emerald-500/40 bg-emerald-500/10"}`}
            data-testid="session-status" data-state={lost ? "lost" : "active"}>
            <span className={`w-2.5 h-2.5 rounded-full ${lost ? "bg-red-500" : "bg-emerald-400 pulse-dot"}`} />
            {lost ? (
              <>
                <span className="text-sm font-semibold text-red-300">{liveMode === "camera" ? "Camera" : "Screen Share"} Lost — Click to reconnect</span>
                <span className="text-xs text-slate-400 hidden sm:inline">Your analysis history and broker chat are kept.</span>
                <Button size="sm" onClick={() => startShare(true)} className="ml-auto bg-red-500 hover:bg-red-400 text-white font-semibold h-8" data-testid="session-reconnect-button">
                  <MonitorUp className="w-4 h-4 mr-1.5" /> Reconnect
                </Button>
              </>
            ) : (
              <>
                <span className="text-sm font-semibold text-emerald-300">{liveMode === "camera" ? "Live Camera Active" : "Screen Share Active"} — Vision {autoScan ? "ON" : "PAUSED"}</span>
                <span className="text-xs text-slate-400 font-mono-num">every {scanSecs}s · {scanCount} scan{scanCount === 1 ? "" : "s"}{voice.loopOn ? " · voice chat on" : ""}</span>
                <span className="ml-auto text-xs text-slate-500 hidden sm:inline">Session persists while you browse other menus</span>
              </>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left: capture */}
          <div className="lg:col-span-7 space-y-4">
            <div className="glass rounded-2xl p-5">
              <div className="flex flex-wrap gap-3 mb-4">
                {!sharing && !canShareScreen ? (
                  <Button onClick={startCamera} className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="live-camera-button" title="Point your camera at a trading screen for continuous AI analysis">
                    <Camera className="w-4 h-4 mr-2" /> Live Camera
                  </Button>
                ) : !sharing ? (
                  <Button onClick={startShare} data-testid="screen-share-toggle-button" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
                    <MonitorUp className="w-4 h-4 mr-2" /> Share Screen
                  </Button>
                ) : (
                  <>
                    <Button onClick={captureFrame} data-testid="capture-frame-button" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
                      <Camera className="w-4 h-4 mr-2" /> Capture Frame
                    </Button>
                    <Button onClick={toggleAutoScan} variant="outline" data-testid="auto-scan-toggle"
                      className={autoScan ? "border-emerald-500 text-emerald-400" : "border-white/15 text-slate-300"}>
                      <Bell className="w-4 h-4 mr-2" /> Live AI {autoScan ? "ON" : "OFF"}
                    </Button>
                    <div className="flex items-center rounded-md border border-white/15 overflow-hidden h-10" data-testid="scan-interval-control" title="Live scan interval">
                      {SCAN_OPTIONS.map((sec) => (
                        <button key={sec} type="button" onClick={() => setInterval_(sec)} data-testid={`scan-interval-${sec}`} aria-pressed={scanSecs === sec}
                          className={`px-3 h-full text-xs font-mono-num transition-colors ${scanSecs === sec ? "bg-emerald-500 text-[#07090E] font-semibold" : "text-slate-300 hover:bg-white/5"}`}>{sec}s</button>
                      ))}
                    </div>
                    <Button onClick={voice.toggle} variant="outline" data-testid="voice-broker-toggle" title="Voice broker"
                      className={voice.enabled ? "border-cyan-500 text-cyan-400" : "border-white/15 text-slate-500"}>
                      <Volume1 className={`w-4 h-4 mr-2 ${voice.speaking ? "animate-pulse" : ""}`} /> Voice {voice.enabled ? "ON" : "OFF"}
                    </Button>
                    <Button onClick={toggleMute} variant="outline" size="icon" data-testid="alert-mute-toggle" aria-label={muted ? "Unmute alerts" : "Mute alerts"}
                      title={muted ? "Alert sound muted" : "Alert sound on"}
                      className={muted ? "border-white/15 text-slate-500" : "border-white/15 text-amber-400"}>
                      {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </Button>
                    <Button onClick={stopShare} variant="outline" className="border-white/15 text-slate-300" data-testid="stop-share-button">Stop</Button>
                  </>
                )}
                <Button onClick={() => fileRef.current?.click()} variant="outline" className="border-white/15 text-slate-300" data-testid="upload-chart-button">
                  <Upload className="w-4 h-4 mr-2" /> Upload Chart
                </Button>
                <Button onClick={toggleVoiceChat} variant="outline" data-testid="voice-ask-button" title="Two-way voice conversation with the broker"
                  className={voice.loopOn ? "border-red-500 text-red-400" : "border-white/15 text-slate-300"}>
                  {voice.loopOn ? <MicOff className="w-4 h-4 mr-2" /> : <Mic className="w-4 h-4 mr-2" />} {voice.loopOn ? "Stop voice chat" : "Voice chat"}
                </Button>
                {!canShareScreen && (
                  <Button onClick={() => cameraRef.current?.click()} variant="outline" className="border-white/15 text-slate-300" data-testid="camera-capture-button">
                    <Camera className="w-4 h-4 mr-2" /> Take Photo
                  </Button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="chart-file-input" />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onUpload} data-testid="chart-camera-input" />
              </div>
              {!canShareScreen && !sharing && (
                <div className="mt-3 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2.5 text-xs text-cyan-200" data-testid="ios-share-notice">
                  <b>iPhone/iPad:</b> Safari can't capture the screen, so use <b>Live Camera</b> — point your phone at your trading monitor and the AI analyzes it every {scanSecs}s with voice guidance, exactly like screen share. Or take a screenshot and tap <b>Upload Chart</b> / <b>Take Photo</b> for a one-off analysis.
                </div>
              )}
              <div className="hidden">
              </div>

              <div className="relative rounded-xl overflow-hidden bg-[#0D111A] border border-white/10 aspect-video flex items-center justify-center">
                <video ref={videoRef} className={`w-full h-full object-contain ${sharing ? "block" : "hidden"}`} muted playsInline />
                {!sharing && preview && <img src={preview} alt="chart preview" className="w-full h-full object-contain" data-testid="chart-preview" />}
                {!sharing && !preview && (
                  <div className="text-center text-slate-500 p-8">
                    <ScanLine className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Share your screen or upload a trading chart to begin analysis</p>
                  </div>
                )}
              </div>
              {preview && !sharing && (
                <p className="text-xs text-emerald-400 mt-2">Chart ready for analysis · source: {source}</p>
              )}
              {sharing && autoScan && (
                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1.5 flex-wrap" data-testid="auto-scan-status">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full pulse-dot" /> Live AI · capturing every {scanSecs}s · {scanCount} scan{scanCount === 1 ? "" : "s"}{lastScanAt ? ` · last ${lastScanAt.toLocaleTimeString()}` : " · first analysis running…"} · alerts ≥{ALERT_THRESHOLD}% · voice {voice.enabled ? "on" : "off"}
                </p>
              )}
              {sharing && !autoScan && <p className="text-xs text-amber-400 mt-2" data-testid="auto-scan-paused">Live AI paused — turn it on to resume automatic analysis.</p>}
              {voice.loopOn && (
                <div className={`mt-3 rounded-xl border p-3 flex items-center gap-3 ${voice.status === "listening" || voice.status === "paused" ? "border-emerald-500/50 bg-emerald-500/10" : voice.status === "speaking" ? "border-cyan-500/50 bg-cyan-500/10" : voice.status === "analyzing" ? "border-amber-500/50 bg-amber-500/10" : "border-white/10 bg-white/[0.03]"}`} data-testid="voice-status" data-status={voice.status}>
                  {(voice.status === "listening" || voice.status === "paused") && <><span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" /></span><Mic className="w-4 h-4 text-emerald-400" /><span className="text-sm font-semibold text-emerald-300">Listening…</span><span className="text-xs text-slate-400">ask your broker anything</span></>}
                  {voice.status === "speaking" && <><Volume1 className="w-4 h-4 text-cyan-400 animate-pulse" /><span className="text-sm font-semibold text-cyan-300">AI Speaking…</span><span className="text-xs text-slate-400">mic paused to avoid echo</span></>}
                  {voice.status === "analyzing" && <><Loader2 className="w-4 h-4 text-amber-400 animate-spin" /><span className="text-sm font-semibold text-amber-300">Analyzing…</span><span className="text-xs text-slate-400">checking live data</span></>}
                  {voice.wake && voice.status === "listening" && (
                    <button type="button" onClick={voice.nudge} className="ml-auto text-xs text-amber-300 hover:text-amber-200 underline-offset-2 hover:underline" data-testid="voice-wake-prompt">Tap to speak or say anything to continue</button>
                  )}
                  <button type="button" onClick={toggleVoiceChat} className={`${voice.wake && voice.status === "listening" ? "ml-2" : "ml-auto"} text-xs text-slate-400 hover:text-red-400`} data-testid="voice-status-stop">Stop</button>
                </div>
              )}
              {liveAlert && (
                <div className="mt-3 rounded-xl border border-amber-500/50 bg-amber-500/10 p-4 flex items-start gap-3 fade-up" data-testid="live-alert-banner">
                  <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0 animate-pulse" />
                  <div className="flex-1">
                    <p className="font-display font-bold text-amber-300">URGENT · {liveAlert.rec} {liveAlert.symbol} · {liveAlert.prob}% win probability</p>
                    <p className="text-sm text-slate-200 font-mono-num">Entry {liveAlert.entry ?? "—"} · Target {liveAlert.target ?? "—"} · Stop {liveAlert.stop ?? "—"} · {liveAlert.time}</p>
                  </div>
                  <button onClick={() => setLiveAlert(null)} className="text-slate-400 hover:text-white text-xs" data-testid="live-alert-dismiss">Dismiss</button>
                </div>
              )}
              {(liveLog.length > 0 || voiceQ) && (
                <div className="mt-3 rounded-xl bg-white/[0.03] border border-white/10 p-3" data-testid="live-voice-log">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Volume1 className="w-3 h-3" /> Live broker voice</p>
                  {voiceQ && (
                    <div className="mb-2 text-xs border-b border-white/5 pb-2" data-testid="voice-qa">
                      <p className="text-slate-300"><span className="text-slate-500">You:</span> {voiceQ.q}</p>
                      <p className="text-emerald-300 mt-0.5"><span className="text-slate-500">Broker:</span> {voiceQ.loading ? "thinking…" : (voiceQ.a || voiceQ.error || "—").replace(/[*_#>`]/g, "").slice(0, 400)}</p>
                    </div>
                  )}
                  <ul className="space-y-1 max-h-32 overflow-y-auto">
                    {liveLog.map((x) => <li key={x.id} className="text-xs text-slate-300"><span className="text-slate-500 font-mono-num mr-2">{x.time}</span>{x.text}</li>)}
                  </ul>
                </div>
              )}
            </div>

            <div className="glass rounded-2xl p-5 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 mb-1 block">Ticker symbol (for live data)</label>
                  <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="e.g. AAPL, BTC-USD, TSLA"
                    className="bg-white/5 border-white/10 font-mono-num" data-testid="symbol-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 mb-1 block">Chart timeframe</label>
                  <div className="flex flex-wrap gap-1" data-testid="timeframe-selector">
                    {TIMEFRAMES.map((tf) => (
                      <button key={tf} type="button" data-testid={`timeframe-${tf}`} aria-pressed={timeframe === tf}
                        onClick={() => { const v = timeframe === tf ? "" : tf; setTimeframe(v); localStorage.setItem("copilot_timeframe", v); }}
                        className={`px-2.5 h-9 rounded-md text-xs font-mono-num border transition-colors ${timeframe === tf ? "bg-emerald-500 text-[#07090E] border-emerald-500 font-semibold" : "bg-white/5 border-white/10 text-slate-300 hover:border-white/30"}`}>
                        {tf.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{timeframe ? "Backtest & indicators run on this timeframe." : "Auto-detected from the chart if left blank."}</p>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 mb-1 block">Note (optional)</label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. considering a long here"
                  className="bg-white/5 border-white/10" data-testid="note-input" />
              </div>
              <Button onClick={analyze} disabled={loading || !preview} data-testid="analyze-button"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold h-11">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing chart…</> : "Analyze with AI"}
              </Button>
            </div>
            <CreditMeter refreshKey={usageKey} live={sharing && autoScan} scanSecs={scanSecs} />
            <Disclaimer compact />
          </div>

          {/* Right: results */}
          <div className="lg:col-span-5 space-y-4">
            {alerts.length > 0 && (
              <div className="glass rounded-2xl p-5" data-testid="alerts-panel">
                <div className="flex items-center justify-between mb-3">
                  <p className="flex items-center gap-2 text-sm font-semibold"><Zap className="w-4 h-4 text-amber-400" /> High-Probability Alerts</p>
                  <button onClick={playChime} className="text-xs text-slate-400 hover:text-amber-400 transition-colors" data-testid="test-chime-button">Test sound</button>
                </div>
                <ul className="space-y-3">
                  {alerts.map((a) => (
                    <li key={a.id} className="border-b border-white/5 pb-3 last:border-0 last:pb-0" data-testid={`alert-item-${a.id}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-mono-num">{a.symbol}</span>
                        <span className={a.rec === "BUY" ? "text-emerald-400 font-semibold" : "text-red-400 font-semibold"}>{a.rec} {a.prob}%</span>
                        <span className="text-slate-500 text-xs font-mono-num">{a.time}</span>
                      </div>
                      {a.reasoning && <div className="mt-2"><SignalReasoning reasoning={a.reasoning} recommendation={a.rec} probability={a.prob} testId={`alert-reasoning-${a.id}`} /></div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!r && (
              <div className="glass rounded-2xl p-8 text-center text-slate-500 h-full flex flex-col items-center justify-center min-h-[300px]">
                <Radar className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm">AI guidance, probabilities and levels will appear here after analysis.</p>
              </div>
            )}
            {r && (
              <div className="space-y-4 fade-up" data-testid="analysis-result">
                <ForecastReport result={r} signal={result.signal} onConfirm={confirmDetection} onSpeak={(text, key) => voice.speak(text, { key: `sig_${key}`, urgent: true })} />
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400">Signal · {result.symbol || r.detected_symbol}</p>
                      <p className={`font-display text-3xl font-extrabold flex items-center gap-2 ${recColor}`} data-testid="recommendation">
                        <RecIcon className="w-7 h-7" /> {r.recommendation}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400 uppercase tracking-wider">Confidence</p>
                      <p className="font-mono-num text-2xl font-bold">{r.confidence}%</p>
                    </div>
                  </div>
                  <div className="flex justify-around py-2 mb-1">
                    <ProbabilityGauge label={r.probability?.backtested ? "Buy win-rate" : "Buy (AI estimate)"} value={r.buy_probability} color="#10B981" testId="win-probability-gauge" />
                    <ProbabilityGauge label={r.probability?.backtested ? "Sell win-rate" : "Sell (AI estimate)"} value={r.sell_probability} color="#EF4444" testId="loss-probability-gauge" />
                  </div>
                  <p className={`text-center text-xs mb-3 ${r.probability?.backtested ? "text-emerald-300" : "text-amber-300"}`} data-testid="probability-source-line">
                    {r.probability?.backtested ? `Backtested: ${r.probability.display}` : "AI confidence estimate — not backtested"}
                  </p>
                  <SignalReasoning reasoning={r.reasoning} recommendation={r.recommendation}
                    probability={Math.max(r.buy_probability || 0, r.sell_probability || 0)} />
                </div>

                <SignalEvidence result={r} />

                <TradePlan plan={r.trade_plan} symbol={result.symbol || r.detected_symbol} currency={result.market?.currency && result.market.currency !== "USD" ? result.market.currency : ""} />

                <div className="glass rounded-2xl p-5" data-testid="broker-panel">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold flex items-center gap-2"><Bot className="w-4 h-4 text-emerald-400" /> Your AI Broker</p>
                    {broker?.sessionId && <a href={`/broker?session=${broker.sessionId}`} className="text-xs text-slate-400 hover:text-emerald-400" data-testid="broker-open-full">Open full chat →</a>}
                  </div>
                  {broker?.loading && (
                    <p className="text-sm text-slate-400 flex items-center gap-2" data-testid="broker-loading"><Loader2 className="w-4 h-4 animate-spin text-emerald-400" /> Broker is preparing your trade breakdown…</p>
                  )}
                  {!broker && (
                    <Button onClick={() => openBroker(result.analysis_id)} variant="outline" className="border-white/15 text-slate-300 w-full" data-testid="broker-open-button">
                      <Bot className="w-4 h-4 mr-2" /> Talk to your broker about this signal
                    </Button>
                  )}
                  {broker?.sessionId && <BrokerChat sessionId={broker.sessionId} initialMessages={broker.messages} refreshKey={brokerRefresh} className="h-[520px]" />}
                </div>

                <div className="glass rounded-2xl p-5 grid grid-cols-3 gap-3 text-center">
                  <Level label="Entry" value={r.entry} color="text-white" />
                  <Level label="Stop Loss" value={r.stop_loss} color="text-red-400" />
                  <Level label="Take Profit" value={r.take_profit} color="text-emerald-400" />
                </div>

                <div className="glass rounded-2xl p-5 space-y-3">
                  <Row label="Trend" value={<span className="capitalize">{r.trend}</span>} />
                  <Row label="Timeframe" value={r.timeframe} />
                  <Row label="Risk/Reward" value={r.risk_reward} />
                  <Row label="News sentiment" value={<span className="capitalize">{r.news_sentiment}</span>} />
                  {r.patterns?.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Patterns</p>
                      <div className="flex flex-wrap gap-2">{r.patterns.map((p, i) => (<span key={i} className="text-xs bg-white/5 border border-white/10 rounded-full px-2.5 py-1">{p}</span>))}</div>
                    </div>
                  )}
                  {r.indicators?.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Indicators</p>
                      <ul className="text-sm text-slate-300 space-y-1">{r.indicators.map((x, i) => (<li key={i}>• {x}</li>))}</ul>
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Rationale</p>
                    <p className="text-sm text-slate-300 leading-relaxed">{r.rationale}</p>
                  </div>
                </div>

                {result.market?.news?.length > 0 && (
                  <div className="glass rounded-2xl p-5">
                    <p className="flex items-center gap-2 text-sm font-semibold mb-3"><Newspaper className="w-4 h-4 text-cyan-400" /> Live News</p>
                    <ul className="space-y-2">
                      {result.market.news.slice(0, 4).map((n, i) => (
                        <li key={i} className="text-sm">
                          {n.link ? <a href={n.link} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-cyan-400">{n.title}</a> : <span className="text-slate-300">{n.title}</span>}
                          {n.publisher && <span className="text-slate-500 text-xs block">{n.publisher}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Level({ label, value, color }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{label}</p>
      <p className={`font-mono-num text-lg font-bold ${color}`}>{value ?? "—"}</p>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="text-white font-medium">{value ?? "—"}</span>
    </div>
  );
}
